import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LessonPlayer from '../../pages/system/tutorials/components/LessonPlayer';
import LessonList from '../../pages/system/tutorials/components/LessonList';
import useTutorialProgress from '../../pages/system/tutorials/useTutorialProgress';
import ColorPalette from '../../colorpalette/ColorPalette';

/**
 * Tutoriais em modal, sem sair da tela: player da lição e, se houver mais de uma, a lista ao lado.
 * @param {Boolean} open
 * @param {Function} onClose
 * @param {Array} lessons - lições já filtradas (mesmo formato de /data/tutorials.json)
 * @param {String} title - título da lista (ex.: "Nesta tela")
 */
const TutorialDialog = ({ open, onClose, lessons, title = 'Nesta tela' }) => {
  const navigate = useNavigate();
  const fullScreen = useMediaQuery('(max-width: 768px)');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playToken, setPlayToken] = useState(0);
  // Carrega ao abrir: o atalho fica montado no topo de todas as telas
  const { progressById, track } = useTutorialProgress(open);
  const hasList = lessons.length > 1;
  const lesson = lessons[currentIndex] || lessons[0] || null;

  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setPlayToken(0);
    }
  }, [open]);

  const goToLesson = (index) => {
    if (index < 0 || index >= lessons.length) return;
    setCurrentIndex(index);
    setPlayToken((token) => token + 1);
  };

  const openAllTutorials = () => {
    onClose();
    navigate('/system/tutorials');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={hasList ? 'lg' : 'md'}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '20px' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: { xs: 2, md: 3 }, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolOutlinedIcon sx={{ color: ColorPalette.blueMain, fontSize: 22 }} />
          <Typography sx={{ fontWeight: 700, color: '#1A1A2E', fontSize: '1.05rem' }}>Como usar esta tela</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={openAllTutorials}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', color: ColorPalette.blueMain }}
          >
            Ver todos os tutoriais
          </Button>
          <IconButton onClick={onClose} size="small" aria-label="Fechar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
        {lesson && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: hasList ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)' },
              gap: { xs: 3, lg: 4 },
              alignItems: 'start'
            }}
          >
            <LessonPlayer
              lesson={lesson}
              index={currentIndex}
              total={lessons.length}
              onPrev={() => goToLesson(currentIndex - 1)}
              onNext={() => goToLesson(currentIndex + 1)}
              nextTitle={lessons[currentIndex + 1]?.title}
              playToken={playToken}
              autoPlay={playToken > 0}
              progress={progressById[lesson.id] || null}
              onTrack={track}
              source="help"
            />
            {hasList && (
              <LessonList chapter={{ title }} lessons={lessons} currentIndex={currentIndex} onSelect={goToLesson} progressById={progressById} />
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TutorialDialog;
